/**
 * analytics — consent-gated PostHog wrapper (product analytics + session
 * replay for the tester round, 23 Jul 2026).
 *
 * HARD RULES (see privacy policy + lib/consents.js on the backend):
 *  - PostHog NEVER captures until the user has granted the 'analytics'
 *    consent UNDER THE CURRENT POLICY VERSION. The gate lives in
 *    components/AnalyticsGate.jsx; this module just enforces mechanics.
 *  - Session replay masks all inputs (maskAllInputs) — nothing typed is
 *    ever recorded.
 *  - Users are identified by INTERNAL USER ID ONLY. Never email, never a
 *    name, never any profile property. Never a shadow-child identifier —
 *    children have no logins, so only the logged-in adult id ever reaches
 *    here, and it must stay that way.
 *  - This is UX/product learning ONLY. The in-house user_events pipeline
 *    remains the source of truth for anything sponsor-facing (attribution,
 *    viewability, QR value) — do not send sponsor metrics to PostHog.
 *
 * The project API key is PUBLIC and write-only (it can only ingest events),
 * so shipping a default in source is safe; REACT_APP_POSTHOG_KEY overrides
 * it. An empty REACT_APP_POSTHOG_KEY=off disables analytics entirely.
 */
import posthog from 'posthog-js';
import { sanitiseEvent, sanitiseUrl } from './urlSanitise';

const POSTHOG_KEY =
  process.env.REACT_APP_POSTHOG_KEY === 'off'
    ? ''
    : process.env.REACT_APP_POSTHOG_KEY ||
      'phc_q9CBdqCBN9QbPAPpHTAU4ypDKyQBcyGArRHteP5GWr7H';

// EU Cloud (Frankfurt) — consent-clean posture: first-party, EU-hosted,
// nothing shared with ad networks.
const POSTHOG_HOST =
  process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';

let initialised = false;

const init = () => {
  if (initialised || !POSTHOG_KEY) return initialised;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Consent gate: even once initialised, nothing is captured until
    // enableAnalytics() explicitly opts in.
    opt_out_capturing_by_default: true,
    // Only consented, identified users get person profiles.
    person_profiles: 'identified_only',
    autocapture: true,
    // SPA: capture a $pageview on every react-router history change.
    capture_pageview: 'history_change',
    capture_pageleave: true,
    session_recording: {
      maskAllInputs: true, // never record anything typed
      // B-65 review: replayed network calls carry URLs (e.g.
      // /auth/reset-password/check?token=...): strip secrets.
      maskCapturedNetworkRequestFn: (req) => (req && req.name ? { ...req, name: sanitiseUrl(req.name) } : req),
    },
    // B-65 security review: confirm / "This wasn't me" / reset / invite links
    // carry single-use secrets in the URL. Strip them from every event
    // ($current_url, $pathname, $referrer, $initial_*, replay snapshots) before
    // anything leaves the browser. Pure helper + tests: lib/urlSanitise.js.
    before_send: (event) => sanitiseEvent(event),
    // No PostHog-run surveys — we run our own questionnaire.
    disable_surveys: true,
  });
  initialised = true;
  return true;
};

/**
 * Called by AnalyticsGate when a logged-in user HAS analytics consent under
 * the current policy version. Safe to call repeatedly.
 * @param {string|number} userId — INTERNAL user id only. No email. Ever.
 */
export const enableAnalytics = (userId) => {
  if (!init()) return;
  if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing();
  posthog.startSessionRecording(); // no-op if already recording
  if (userId != null) posthog.identify(String(userId));
};

/**
 * Called on consent revoke (and for impersonation sessions). Immediate:
 * stops replay and all capture. The opt-out is persisted by posthog-js, so
 * a reload stays opted out.
 */
export const disableAnalytics = () => {
  if (!initialised) return;
  posthog.stopSessionRecording();
  if (!posthog.has_opted_out_capturing()) posthog.opt_out_capturing();
};

/**
 * Called on logout: stop capture AND drop the identity/device state so the
 * next account on a shared device is never linked to this one.
 */
export const resetAnalytics = () => {
  if (!initialised) return;
  disableAnalytics();
  posthog.reset();
};
